import { WORKSPACE_CONTROLS, type WorkspaceControls, type WorkspaceControlsSet } from '@arcnet/contracts';
import { Switch } from '../../components/ui/Form.js';

/**
 * What the agent workspace offers (0153), as one editor for its three levels: the ACCOUNT, a
 * QUEUE and an AGENT. A switch per control at every level, as everywhere else on the platform
 * (owner, 6 October 2026: "these should be switches"); it shows what applies.
 *
 * Below the account a control inherits until it is changed. The switch then shows the inherited
 * answer, captioned with where it comes from; flipped, it is set here, and a Reset puts it back.
 * Flipping it back to the inherited answer inherits again rather than pinning a copy, so a later
 * change at the level above still reaches it.
 */
const GROUPS = [...new Set(WORKSPACE_CONTROLS.map((c) => c.group))];

export function WorkspaceControlsEditor({ value, onChange, inherit, inheritLabel, idPrefix, disabled }: {
  value: WorkspaceControlsSet;
  onChange: (next: WorkspaceControlsSet) => void;
  /** What applies where this level sets nothing; null for the account itself. */
  inherit: WorkspaceControls | null;
  /** "Account default", "From their queues". */
  inheritLabel?: string;
  idPrefix: string;
  disabled?: boolean;
}) {
  const set = (key: keyof WorkspaceControls, v: boolean | null): void => {
    const next = { ...value };
    if (v === null) delete next[key]; else next[key] = v;
    onChange(next);
  };
  return (
    <div className="arc-wsc">
      {GROUPS.map((g) => (
        <div key={g} className="arc-wsc-group">
          <span className="arc-wsc-head">{g}</span>
          {WORKSPACE_CONTROLS.filter((c) => c.group === g).map((c) => {
            const id = `${idPrefix}-${c.key}`;
            const own = value[c.key];
            return (
              <div key={c.key} className="arc-wsc-row">
                <div className="arc-stack" style={{ gap: 1, minWidth: 0 }}>
                  <label htmlFor={id} className="arc-wsc-label">{c.label}</label>
                  <span className="arc-wsc-hint">{c.hint}</span>
                </div>
                {inherit === null ? (
                  <Switch id={id} checked={own ?? c.default} disabled={disabled ?? false} onChange={(v) => set(c.key, v)} />
                ) : (
                  <div className="arc-wsc-ctl">
                    <Switch id={id} checked={own ?? inherit[c.key]} disabled={disabled ?? false}
                      onChange={(v) => set(c.key, v === inherit[c.key] ? null : v)} />
                    {own === undefined
                      ? <span className="arc-wsc-src">{inheritLabel ?? 'Inherited'}</span>
                      : (
                        <button type="button" className="arc-wsc-reset" disabled={disabled}
                          title={`Back to ${(inheritLabel ?? 'the default').toLowerCase()} (${inherit[c.key] ? 'on' : 'off'})`}
                          onClick={() => set(c.key, null)}>
                          Reset
                        </button>
                      )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
